import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Delete, ShieldCheck, Loader2, Mail, ArrowLeft } from "lucide-react";
import ResetPanel from "@/components/login/ResetPanel";

const bgUrl =
  "https://images.pexels.com/photos/15721860/pexels-photo-15721860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

// Pull a server detail object out of an axios error, regardless of whether
// FastAPI returned `{detail: "..."}` or `{detail: {code, message}}`.
function readDetail(err) {
  const d = err?.response?.data?.detail;
  if (!d) return null;
  if (typeof d === "string") return { code: null, message: d };
  return { code: d.code || null, message: d.message || JSON.stringify(d) };
}

export default function Login() {
  const { login, requestOtp, requestPinReset, verifyPinReset, user } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  // OTP step state — when the backend tells us the device is untrusted we
  // hold on to the PIN, request an email code, and switch UI to OTP entry.
  const [otpStep, setOtpStep] = useState(null);
  // otpStep = null | { pin, sentTo, expiresIn, otpCode, sending, expiresAt }
  const otpCode = otpStep?.otpCode || "";
  // PIN-reset (forgot pin) state.
  const [resetStep, setResetStep] = useState(null);
  // resetStep = null | { phase: "code" | "newpin", sentTo, expiresIn, code, newPin, confirmPin, sending }

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  // ------------------------------------------------------------------ PIN
  const submit = async (value) => {
    if (loading) return;
    if (!value || value.length < 6) return;
    setLoading(true);
    try {
      await login(value);
      toast.success("Welcome");
      nav("/");
    } catch (e) {
      const det = readDetail(e);
      if (det?.code === "otp_required") {
        // New device — switch to OTP screen and trigger the email.
        await beginOtp(value);
      } else {
        toast.error(det?.message || "Login failed");
        setPin("");
      }
    } finally {
      setLoading(false);
    }
  };

  const beginOtp = async (pinValue) => {
    setOtpStep({ pin: pinValue, sending: true, otpCode: "" });
    try {
      const data = await requestOtp(pinValue);
      setOtpStep({
        pin: pinValue,
        sentTo: data.sent_to,
        expiresIn: data.expires_in,
        otpCode: "",
        sending: false,
      });
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (e) {
      const det = readDetail(e);
      toast.error(det?.message || "Failed to send code");
      setOtpStep(null);
      setPin("");
    }
  };

  const submitOtp = async () => {
    const code = (otpStep?.otpCode || "").trim();
    if (code.length < 6) return;
    setLoading(true);
    try {
      await login(otpStep.pin, code);
      toast.success("Device verified · welcome");
      nav("/");
    } catch (e) {
      const det = readDetail(e);
      toast.error(det?.message || "Wrong or expired code");
      setOtpStep((s) => (s ? { ...s, otpCode: "" } : s));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!otpStep?.pin || otpStep.sending) return;
    await beginOtp(otpStep.pin);
  };

  const cancelOtp = () => {
    setOtpStep(null);
    setPin("");
  };

  // ------------------------------------------------------------- Forgot PIN
  const beginPinReset = async () => {
    setResetStep({ phase: "code", sending: true, code: "", newPin: "", confirmPin: "" });
    try {
      const data = await requestPinReset();
      setResetStep({
        phase: "code",
        sentTo: data.sent_to,
        expiresIn: data.expires_in,
        code: "",
        newPin: "",
        confirmPin: "",
        sending: false,
      });
      toast.success(`Reset code sent to ${data.sent_to}`);
    } catch (e) {
      const det = readDetail(e);
      toast.error(det?.message || "Failed to send reset email");
      setResetStep(null);
    }
  };

  const submitPinReset = async () => {
    if (!resetStep) return;
    if (resetStep.phase === "code") {
      if (resetStep.code.length < 6) return;
      // Advance to new-pin entry — the code is sent up with the new pin in one
      // call, so we just hold it and switch view.
      setResetStep({ ...resetStep, phase: "newpin" });
      return;
    }
    // phase === "newpin"
    const { code, newPin, confirmPin } = resetStep;
    if (newPin.length < 4) {
      toast.error("New PIN must be at least 4 digits");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("PINs don't match");
      return;
    }
    setLoading(true);
    try {
      await verifyPinReset(code, newPin);
      toast.success("PIN reset · sign in with your new PIN");
      setResetStep(null);
      setPin("");
    } catch (e) {
      const det = readDetail(e);
      toast.error(det?.message || "Wrong or expired code");
      setResetStep((r) => (r ? { ...r, phase: "code", code: "" } : r));
    } finally {
      setLoading(false);
    }
  };

  const press = (d) => {
    if (loading) return;
    if (resetStep) {
      if (resetStep.phase === "code") {
        setResetStep((r) =>
          r ? { ...r, code: (r.code + d).slice(0, 6) } : r
        );
      } else {
        // newpin phase — type into newPin first, then confirmPin
        setResetStep((r) => {
          if (!r) return r;
          if (r.newPin.length < 6) return { ...r, newPin: r.newPin + d };
          return { ...r, confirmPin: (r.confirmPin + d).slice(0, 6) };
        });
      }
      return;
    }
    if (otpStep) {
      setOtpStep((s) =>
        s ? { ...s, otpCode: (s.otpCode + d).slice(0, 6) } : s
      );
      return;
    }
    const next = (pin + d).slice(0, 8);
    setPin(next);
    if (next.length === 6) {
      // auto-submit on 6 digits
      submit(next);
    }
  };

  const handleKey = (e) => {
    if (/^[0-9]$/.test(e.key)) press(e.key);
    else if (e.key === "Backspace") {
      if (resetStep) {
        setResetStep((r) => {
          if (!r) return r;
          if (r.phase === "code") return { ...r, code: r.code.slice(0, -1) };
          // newpin: backspace confirmPin first then newPin
          if (r.confirmPin) return { ...r, confirmPin: r.confirmPin.slice(0, -1) };
          return { ...r, newPin: r.newPin.slice(0, -1) };
        });
      } else if (otpStep) {
        setOtpStep((s) => (s ? { ...s, otpCode: s.otpCode.slice(0, -1) } : s));
      } else {
        setPin((p) => p.slice(0, -1));
      }
    } else if (e.key === "Enter") {
      if (resetStep) submitPinReset();
      else if (otpStep) submitOtp();
      else submit(pin);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, loading, otpStep, resetStep]);

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-2"
      style={{ fontFeatureSettings: '"tnum"' }}
    >
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3 text-white">
            <img
              src="/oz-bakery-logo.png"
              alt="Oz Bakery"
              className="h-14 w-14 rounded-lg object-contain bg-white/15 backdrop-blur p-1"
            />
            <div>
              <div className="font-display text-xl font-bold">Oz Bakery</div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-80">
                Wholesale Bakery Operations · Est 1996
              </div>
            </div>
          </div>
          <div className="max-w-md space-y-3">
            <div className="text-xs uppercase tracking-[0.22em] opacity-80">
                Built for SALSA / BRCGS Readiness
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
              From flour to fork — every batch, every step, traced.
            </h1>
            <p className="text-base opacity-90 leading-relaxed">
              Goods intake, recipes, production, packing and dispatch — all linked
              with one-step-back and one-step-forward recall capability.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          {resetStep ? (
            <ResetPanel
              resetStep={resetStep}
              press={press}
              loading={loading}
              onBack={() => setResetStep(null)}
              onSubmit={submitPinReset}
              setResetStep={setResetStep}
            />
          ) : otpStep ? (
            // --------------------------------- OTP step ---------------------
            <div data-testid="otp-screen">
              <button
                type="button"
                onClick={cancelOtp}
                className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-4"
                data-testid="otp-back"
              >
                <ArrowLeft className="h-3 w-3" /> Back to PIN
              </button>
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                  <Mail className="h-7 w-7 text-amber-800" />
                </div>
              </div>
              <div className="text-center mb-6">
                <div className="font-display text-2xl font-bold tracking-tight">
                  Verify this device
                </div>
                <div className="text-stone-600 mt-1.5 text-sm">
                  {otpStep.sending ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending code…
                    </span>
                  ) : (
                    <>
                      We sent a 6-digit code to{" "}
                      <span className="font-semibold text-stone-800" data-testid="otp-recipient">
                        {otpStep.sentTo}
                      </span>
                      . It expires in {Math.round((otpStep.expiresIn || 600) / 60)} min.
                    </>
                  )}
                </div>
              </div>

              <div
                className="flex justify-center gap-2 mb-6"
                data-testid="otp-display"
              >
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`h-12 w-9 rounded-md border-2 flex items-center justify-center font-display font-bold text-xl ${
                      otpCode.length > i
                        ? "border-amber-500 bg-amber-50 text-amber-900"
                        : "border-stone-300 text-stone-300"
                    }`}
                  >
                    {otpCode[i] || ""}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3" data-testid="otp-numpad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    onClick={() => press(String(n))}
                    disabled={loading || otpStep.sending}
                    data-testid={`otp-key-${n}`}
                    className="h-16 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setOtpStep((s) => (s ? { ...s, otpCode: "" } : s))
                  }
                  className="h-16 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold uppercase tracking-wider"
                  data-testid="otp-clear"
                >
                  Clear
                </button>
                <button
                  onClick={() => press("0")}
                  disabled={loading || otpStep.sending}
                  data-testid="otp-key-0"
                  className="h-16 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
                >
                  0
                </button>
                <button
                  onClick={() =>
                    setOtpStep((s) =>
                      s ? { ...s, otpCode: s.otpCode.slice(0, -1) } : s
                    )
                  }
                  data-testid="otp-backspace"
                  className="h-16 rounded-xl bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
                >
                  <Delete className="h-6 w-6" />
                </button>
              </div>

              <button
                onClick={submitOtp}
                disabled={loading || otpCode.length < 6}
                data-testid="otp-submit"
                className="mt-6 w-full h-14 rounded-xl bg-primary text-white font-semibold text-lg disabled:opacity-50 hover:bg-primary/90 active:scale-[0.99] transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Verify device"
                )}
              </button>

              <button
                type="button"
                onClick={resendOtp}
                disabled={otpStep.sending}
                className="mt-4 w-full text-xs text-stone-500 hover:text-stone-800 underline-offset-2 hover:underline"
                data-testid="otp-resend"
              >
                Didn&apos;t get it? Resend code
              </button>

              <div className="mt-6 text-xs text-stone-500 text-center">
                Future logins on this browser will go straight to the PIN screen.
              </div>
            </div>
          ) : (
            // --------------------------------- PIN step ---------------------
            <>
          <div className="text-center mb-8">
            <div className="font-display text-3xl font-bold tracking-tight">
              Enter your PIN
            </div>
            <div className="text-stone-600 mt-1 text-sm">
              Shop-floor sign-in for production staff & admins.
            </div>
          </div>

          <div
            className="flex justify-center gap-3 mb-8"
            data-testid="pin-display"
          >
            {["pin-dot-0", "pin-dot-1", "pin-dot-2", "pin-dot-3", "pin-dot-4", "pin-dot-5"].map((id, i) => (
              <div
                key={id}
                className={`h-4 w-4 rounded-full border-2 ${
                  pin.length > i ? "bg-primary border-primary" : "border-stone-300"
                }`}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3" data-testid="pin-numpad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                onClick={() => press(String(n))}
                disabled={loading}
                data-testid={`pin-key-${n}`}
                className="h-20 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPin("")}
              data-testid="pin-clear"
              className="h-20 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold uppercase tracking-wider"
            >
              Clear
            </button>
            <button
              onClick={() => press("0")}
              disabled={loading}
              data-testid="pin-key-0"
              className="h-20 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
            >
              0
            </button>
            <button
              onClick={() => setPin((p) => p.slice(0, -1))}
              data-testid="pin-backspace"
              className="h-20 rounded-xl bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
            >
              <Delete className="h-6 w-6" />
            </button>
          </div>

          <button
            onClick={() => submit(pin)}
            disabled={loading || pin.length < 6}
            data-testid="pin-submit"
            className="mt-6 w-full h-14 rounded-xl bg-primary text-white font-semibold text-lg disabled:opacity-50 hover:bg-primary/90 active:scale-[0.99] transition flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </button>

          <button
            type="button"
            onClick={beginPinReset}
            className="mt-4 w-full text-xs text-stone-500 hover:text-stone-800 underline-offset-2 hover:underline"
            data-testid="forgot-pin"
          >
            Forgot admin PIN? Email a reset code
          </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

